import { useEffect, useState } from 'react';
import Reveal from '../../common/Reveal';
import ourStudentsStories from '../../../assets/images/our-students-stories.png';

function hasImageUrl(url) {
  return Boolean(url && String(url).trim());
}

function StudentFeedbackSection({ testimonials, loading, error }) {
  const list = testimonials || [];
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (activeIndex >= list.length) setActiveIndex(0);
  }, [list.length, activeIndex]);

  const active = list[activeIndex];
  const total = list.length;
  const showNav = total > 1;
  const hasAnyImage = list.some((student) => hasImageUrl(student.image));
  const showMainImage = hasImageUrl(active?.image);

  const goPrev = () => setActiveIndex((i) => (i === 0 ? total - 1 : i - 1));
  const goNext = () => setActiveIndex((i) => (i === total - 1 ? 0 : i + 1));

  const navButtonClass =
    'flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-red-500 text-white shadow-md transition hover:bg-red-600';

  const renderBody = () => {
    if (loading && !list.length) {
      return (
        <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-14">
          <div className="h-[320px] w-full animate-pulse rounded-2xl bg-gray-100 sm:h-[400px] lg:h-[360px] xl:h-[480px]" />
          <div className="space-y-4">
            <div className="h-6 w-48 animate-pulse rounded bg-gray-100" />
            <div className="h-10 w-72 animate-pulse rounded bg-gray-100" />
            <div className="h-24 w-full animate-pulse rounded bg-gray-100" />
          </div>
        </div>
      );
    }

    if (error) {
      return <p className="text-center text-sm text-red-600">{error}</p>;
    }

    if (!list.length) {
      return (
        <p className="text-center text-sm text-gray-500">No student testimonials available right now.</p>
      );
    }

    return (
      <div
        className={`${
          hasAnyImage ? 'grid items-center gap-10 lg:grid-cols-2 lg:gap-14' : 'max-w-3xl'
        }`}
      >
        {hasAnyImage && (
          <Reveal variant="right" className="relative mx-auto w-full max-w-md lg:max-w-none">
            <div className="overflow-hidden rounded-2xl">
              {showMainImage ? (
                <img
                  key={active.id}
                  src={active.image}
                  alt={active.name}
                  className="h-[320px] w-full object-cover object-center sm:h-[400px] lg:h-[360px] xl:h-[480px]"
                />
              ) : (
                <div className="h-[320px] w-full bg-gray-100 sm:h-[400px] lg:h-[360px] xl:h-[480px]" aria-hidden />
              )}
            </div>

            {showNav && (
              <div className="absolute right-2 top-1/2 z-10 flex -translate-y-1/2 flex-col items-center gap-3 lg:right-0 lg:translate-x-1/4 xl:translate-x-1/2">
                <button type="button" onClick={goPrev} className={navButtonClass} aria-label="Previous student">
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
                  </svg>
                </button>

                {list.map((student, index) =>
                  hasImageUrl(student.thumb) ? (
                    <button
                      key={student.id}
                      type="button"
                      onClick={() => setActiveIndex(index)}
                      className={`overflow-hidden rounded-full ring-2 ring-offset-2 transition ${
                        activeIndex === index
                          ? 'ring-red-500 ring-offset-white'
                          : 'ring-white/80 opacity-80 hover:opacity-100'
                      }`}
                      aria-label={`View ${student.name} testimonial`}
                      aria-current={activeIndex === index ? 'true' : undefined}
                    >
                      <img src={student.thumb} alt="" className="h-11 w-11 object-cover" />
                    </button>
                  ) : null
                )}

                <button type="button" onClick={goNext} className={navButtonClass} aria-label="Next student">
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>
            )}
          </Reveal>
        )}

        <div className="relative lg:pl-2">
          <Reveal variant="left">
            <img
              src={ourStudentsStories}
              alt="Our Students Stories"
              className="h-5 w-auto sm:h-6"
            />
          </Reveal>
          <Reveal variant="left" delay={80}>
            <h2 className="mt-3 text-3xl font-extrabold text-[#1B1B1B] lg:text-[40px]">
              Happy Students Feedback
            </h2>
          </Reveal>

          <Reveal variant="up" delay={140}>
            <span className="mt-6 block h-[50px] w-[60px] text-8xl font-serif leading-none text-amber-400 " aria-hidden>
              &ldquo;
            </span>
          </Reveal>

          <Reveal variant="up" delay={180}>
            <p className="mt-2 text-sm leading-relaxed text-[#000000] sm:text-[16px]">{active.quote}</p>
          </Reveal>

          <Reveal variant="up" delay={220}>
            <div className="mt-6">
              <p className="text-[24px] font-semibold text-[#9369CC]">{active.name}</p>
              <p className="text-[16px] font-medium text-[#6E4F99]">{active.role}</p>
            </div>
          </Reveal>

          {showNav && !hasAnyImage && (
            <div className="mt-8 flex items-center gap-3">
              <button type="button" onClick={goPrev} className={navButtonClass} aria-label="Previous student">
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <span className="text-sm text-gray-500">
                {activeIndex + 1} / {total}
              </span>
              <button type="button" onClick={goNext} className={navButtonClass} aria-label="Next student">
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <section className="overflow-x-hidden border-y border-dashed border-blue-200 bg-white px-4 py-7 sm:px-6 sm:py-8 lg:px-8 lg:py-10">
      <div className="mx-auto max-w-7xl">
        {renderBody()}
      </div>
    </section>
  );
}

export default StudentFeedbackSection;
